import i18next from 'i18next';
import { toast } from 'sonner';
import { useConsentRecordStore } from '@/store/consent-record.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ConsentRecordIntegration {
    constructor(sdkService, explicitConsentService) {
        this.service = sdkService;
        this.explicitConsentService = explicitConsentService;
    }

    async fetchStats() {
        try {
            const response = await this.service.getStats();
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.message, data: null };
        }
    }

    async fetchItems(params = {}) {
        const store = useConsentRecordStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        if (store.clearError) store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const page = params.page || store.paginationData?.currentPage || 1;
            const pageSize = params.pageSize || store.paginationData?.itemsPerPage || 10;
            const search = params.search ?? '';
            const sortBy = params.sortBy || 'createdAt';
            const sortOrder = params.sortOrder || 'desc';
            const columnFilters = params.columnFilters ?? store.columnFilters ?? {};

            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value?.length) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });

            store.setItems(response.data || []);
            if (response.pagination) {
                store.setPaginationData({
                    currentPage: response.pagination.page,
                    totalPages: response.pagination.totalPages,
                    totalItems: response.pagination.total,
                    itemsPerPage: response.pagination.pageSize,
                });
            }
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            return { success: true };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchConsentOptions() {
        try {
            const response = await this.explicitConsentService.getAll();
            return { success: true, data: response.data || [] };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async createItem(data) {
        const store = useConsentRecordStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('gdprConsentRecord:messages.createSuccess', { defaultValue: 'Consent record created' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateItem(id, data) {
        const store = useConsentRecordStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('gdprConsentRecord:messages.updateSuccess', { defaultValue: 'Consent record updated' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteItem(id) {
        const store = useConsentRecordStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('gdprConsentRecord:messages.deleteSuccess', { defaultValue: 'Consent record deleted' }),
            );
            return { success: true };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
